import React, { Component } from 'react';
import { getIn, setIn } from 'formik';
import lodashGet from 'lodash/fp/get';
import lodashValues from 'lodash/fp/values';

import {
  SCREEN_ID_ADMIN_LISTING_VITALS,
} from 'client/constants/screenIds';

import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import UserActions from 'client/actions/User';
import MarketActions from 'client/actions/Market';
import PartnerActions from 'client/actions/Partner';
import AccountManagerActions from 'client/actions/AccountManager';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrPage from 'client/components/SrPage';
import SrText from 'client/components/SrText';
import SrSimpleDropdown, {
  SR_SIMPLE_DROPDOWN_THEME_V2,
} from 'client/components/SrSimpleDropdown';
import SrTourHealthListView from 'client/components/SrTourHealthListView';
import SrLeadHealthDataTable from 'client/components/SrLeadHealthDataTable';
import SrUserSettingDropdownWithSearchBar, {
  SR_USER_SETTING_DROPDOWN_WITH_SEARCH_BAR_THEME_ADMIN,
} from 'client/components/SrUserSettingDropdownWithSearchBar/SrUserSettingDropdownWithSearchBar';

const USER_SETTING_LIST_VIEW = 'listingVitals.listView';

const LIST_VIEW_TOUR_HEALTH = 'TourHealth';
const LIST_VIEW_LEAD_HEALTH = 'LeadHealth';

const LIST_VIEW_OPTIONS = [
  {
    id: LIST_VIEW_TOUR_HEALTH,
    title: 'Tour Health',
  },
  {
    id: LIST_VIEW_LEAD_HEALTH,
    title: 'Lead Health',
  },
];

const LIST_VIEW_FILTER_TO_COMPONENT = {
  [LIST_VIEW_TOUR_HEALTH]: SrTourHealthListView,
  [LIST_VIEW_LEAD_HEALTH]: SrLeadHealthDataTable,
};

const ListViewFilter = ({
  value,
  onChange,
}) => {
  const activeOption = LIST_VIEW_OPTIONS.find((option) => option.id === value);

  return (
    <SrSimpleDropdown
      theme={SR_SIMPLE_DROPDOWN_THEME_V2}
      title='Vital Type: '
      subtitle={activeOption.title}
      options={LIST_VIEW_OPTIONS.map((option) => ({
        ...option,
        isActive: option.id === activeOption.id,
      }))}
      onChange={onChange}
    />
  );
};

@withTransitionHook
@withScreenId(SCREEN_ID_ADMIN_LISTING_VITALS)
@withScrollToTop
@branch({
  currentUser: ['currentUser'],
  marketsById: ['markets', 'byId'],
  partners: ['partners', 'list'],
  accountManagersList: ['accountManagers', 'listWithPartners'],
})
class AdminListingVitalsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Admin | Listing Vitals',
  })

  static fetchData(match, inject) {
    return Promise.all([
      MarketActions.getList(inject),
      PartnerActions.getPartnerList(inject),
      AccountManagerActions.getListWithPartners(inject),
    ]);
  }

  constructor(props) {
    super(props);

    const listView = getIn(props.currentUser.settings, USER_SETTING_LIST_VIEW) || LIST_VIEW_TOUR_HEALTH;

    this.state = {
      listView,
    };
  }

  _onChangeListView = (listView) => {
    this.setState({ listView });

    const {
      dispatch,
      currentUser,
    } = this.props;

    const newUserSettings = setIn({ ...currentUser.settings }, USER_SETTING_LIST_VIEW, listView);
    dispatch(UserActions.updateSettings, newUserSettings);
  }

  render() {
    const {
      listView,
    } = this.state;
    const {
      partners,
      marketsById,
      currentUser,
      accountManagersList,
    } = this.props;

    const markets = lodashValues(marketsById);

    const ListViewComponent = LIST_VIEW_FILTER_TO_COMPONENT[listView];

    return (
      <SrPage navbarProps={{ redirectLogoTo: '/admin' }}>
        <SrBox className='width-90'>
          <SrFlex align='end' justify='between'>
            <SrText
              font='greycliff'
              weight='800'
              fontSize='28px'
              lineHeight='34px'
            >
              Listing Vitals
            </SrText>
            <SrFlex>
              <ListViewFilter
                value={listView}
                onChange={this._onChangeListView}
              />
              <SrUserSettingDropdownWithSearchBar
                title='Account Manager'
                notFoundTitle='No account managers found'
                settingField='listingVitals.accountManagerId'
                dropdownOptions={accountManagersList.map((option) => ({
                  label: option.name,
                  value: option.id,
                }))}
                theme={SR_USER_SETTING_DROPDOWN_WITH_SEARCH_BAR_THEME_ADMIN}
              />
              <SrUserSettingDropdownWithSearchBar
                title='Partner'
                notFoundTitle='No partners found'
                settingField='listingVitals.partnerId'
                dropdownOptions={partners.filter((partner) => !partner.invalidSignup).map((option) => ({
                  label: option.displayName,
                  value: option.id,
                }))}
                theme={SR_USER_SETTING_DROPDOWN_WITH_SEARCH_BAR_THEME_ADMIN}
              />
              <SrUserSettingDropdownWithSearchBar
                title='Market'
                notFoundTitle='No markets found'
                settingField='listingVitals.marketId'
                dropdownOptions={markets.map((option) => ({
                  label: option.displayNameFull,
                  value: option.id,
                }))}
                theme={SR_USER_SETTING_DROPDOWN_WITH_SEARCH_BAR_THEME_ADMIN}
              />
            </SrFlex>
          </SrFlex>
          <ListViewComponent
            accountManagerId={lodashGet('listingVitals.accountManagerId', currentUser.settings)}
            partnerId={lodashGet('listingVitals.partnerId', currentUser.settings)}
            marketId={lodashGet('listingVitals.marketId', currentUser.settings)}
          />
        </SrBox>
      </SrPage>
    );
  }
}

AdminListingVitalsPage.displayName = 'AdminListingVitalsPage';

export default AdminListingVitalsPage;
